import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
} from '../domain/dataModel';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const GROUP_DOT_COLORS = [
  'bg-rose-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-cyan-500',
  'bg-orange-500',
];

function groupDotColor(name) {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h += s.charCodeAt(i);
  return GROUP_DOT_COLORS[h % GROUP_DOT_COLORS.length];
}

function IconChevronRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setGroups([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const memSnap = await getDocs(
          query(
            collection(db, COLLECTIONS.GROUP_MEMBERS),
            where(GROUP_MEMBERS_FIELDS.USER_ID, '==', user.uid),
            where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
          )
        );
        if (cancelled) return;

        const groupIds = [...new Set(memSnap.docs.map((d) => d.data()[GROUP_MEMBERS_FIELDS.GROUP_ID]))];
        if (groupIds.length === 0) {
          setGroups([]);
          return;
        }

        const groupSnaps = await Promise.all(
          groupIds.map((id) => getDoc(doc(db, COLLECTIONS.GROUPS, id)))
        );
        if (cancelled) return;

        const list = [];
        groupIds.forEach((gid, i) => {
          const snap = groupSnaps[i];
          if (snap.exists()) list.push({ id: snap.id, ...snap.data() });
        });
        setGroups(list);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Errore nel caricamento dei gruppi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-bold text-brand-ice">I miei gruppi</h1>
      <p className="text-sm text-brand-ice/70">
        Seleziona un gruppo per vedere la classifica e le partite.
      </p>

      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}

      {groups.length === 0 ? (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="flex flex-col items-center gap-4 p-6 text-center">
            <p className="text-sm text-brand-ice/70">Nessun gruppo ancora</p>
            <Button type="button" variant="outline" onClick={() => navigate('/')}>
              Vai alla Dashboard
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {groups.map((group) => {
            const name = group[GROUPS_FIELDS.NAME] || 'Senza nome';
            const description = group[GROUPS_FIELDS.DESCRIPTION];
            const letter = (name && name[0]?.toUpperCase()) || '?';
            return (
              <li key={group.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/groups/${group.id}`)}
                  className="flex w-full items-center gap-4 rounded-card border border-surface-muted bg-surface-card p-4 text-left transition-colors hover:border-brand-sky/30"
                >
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white ${groupDotColor(name)}`}
                  >
                    {letter}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="font-display text-base font-semibold text-brand-ice">{name}</p>
                    {description ? (
                      <p className="line-clamp-2 text-sm text-brand-ice/60">{description}</p>
                    ) : null}
                  </div>
                  <IconChevronRight className="h-5 w-5 shrink-0 text-brand-ice/40" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
